<template>
	<div v-if="part">
		<span
			class="font-regular w-full text-start flex items-center justify-between group pr-2 has-[.existing-mortgage-btn:hover]:bg-gray-100"
		>
			<AppButton
				variant="ghost"
				class="flex items-center p-2 w-full existing-mortgage-btn justify-start"
				@click="collapsed = !collapsed"
			>
				<span
					class="ht-icon text-2xl text-(--text-secondary) group-hover:text-accent-primary"
					:class="{ 'rotate-270': collapsed }"
				>
					expand_more
				</span>

				<span> {{ partTitle(part) }} </span>
			</AppButton>

			<AppButton
				v-if="!readonly"
				variant="ghost"
				class="size-8 text-(--text-secondary) hover:text-accent-primary cursor-pointer"
				icon="delete"
				aria-label="Verwijder leningdeel"
				@click.stop="emit('delete')"
			/>
		</span>

		<div
			v-show="!collapsed"
			class="px-5 pb-3 pt-1 pl-10 w-full border-t bg-gray-50"
		>
			<div class="grid grid-cols-2 gap-2 gap-y-3 w-fit">
				<AppInput
					v-model="part.repaymentForm"
					label="Aflosvorm"
					type="dropdown"
					:class="['col-1', { 'readonly-bordered-input': readonly }]"
					:options="repaymentFormOptions"
					:readonly
				/>

				<AppInput
					v-model="part.lenderMortgagePartId"
					label="Leningdeelnummer"
					:class="{ 'readonly-bordered-input': readonly }"
					:readonly
				/>

				<AppInput
					v-model="part.amountOriginal"
					label="Oorspronkelijke schuld"
					:class="['col-1', { 'readonly-bordered-input': readonly }]"
					:value-type="ValueType.Currency"
					:precision="2"
					:readonly
				/>

				<div
					class="col-span-2 grid grid-cols-[1fr_auto_1fr] grid-rows-2 gap-x-2 gap-y-1 w-full"
				>
					<AppInput
						v-model="part.startDate"
						label="Ingangsdatum"
						:value-type="ValueType.Date"
						:show-display-date="false"
						:class="{ 'readonly-bordered-input': readonly }"
						:readonly
					/>

					<span class="text-sm self-end py-1">tot</span>

					<AppInput
						v-model="part.endDate"
						label="Einddatum"
						:value-type="ValueType.Date"
						:show-display-date="false"
						:class="{ 'readonly-bordered-input': readonly }"
						:readonly
					/>

					<AppInput
						v-model="looptijd"
						label="Looptijd in maanden"
						:class="['col-3', { 'readonly-bordered-input': readonly }]"
						:value-type="ValueType.Number"
						:readonly
					/>
				</div>

				<AppInput
					v-model="part.interestRate"
					label="Rentepercentage"
					:class="['col-1', { 'readonly-bordered-input': readonly }]"
					:value-type="ValueType.Percentage"
					:readonly
				/>

				<AppInput
					v-model="part.fixedInterestEndDate"
					label="Einddatum rentevastperiode"
					:value-type="ValueType.Date"
					:class="{ 'readonly-bordered-input': readonly }"
					:readonly
				/>

				<div class="grid grid-cols-subgrid col-span-full gap-2">
					<AppInput
						v-model="dossierDataStore.data.financieringsOpzet.passeerdatum"
						label="Passeerdatum"
						:value-type="ValueType.Date"
						:class="{ 'readonly-bordered-input': readonly }"
						:readonly
					/>
					<AppInput
						v-model="part.amountCurrent"
						label="Schuld op passeerdatum"
						:class="{ 'readonly-bordered-input': readonly }"
						:value-type="ValueType.Currency"
						:precision="2"
						:readonly
					/>

					<AppButton
						v-if="calculatedAmountCurrent !== undefined && !readonly"
						variant="primary"
						class="self-end justify-self-end col-span-full"
						@click="applyAmountCurrent"
					>
						Berekende schuld overnemen (€
						{{ modelToInput(calculatedAmountCurrent) }})
					</AppButton>
				</div>

				<AppInput
					v-model="part.interestDeductionLaw"
					label="Overgangsrecht"
					type="option"
					:options="interestDeductionLawOptions"
					:class="['col-1', { 'readonly-bordered-input': readonly }]"
					:readonly
				/>

				<AppInput
					v-if="
						part.interestDeductionLaw === 'old' ||
						part.interestDeductionLaw === 'new' ||
						part.interestDeductionEndDate
					"
					v-model="part.interestDeductionEndDate"
					label="Einddatum renteaftrek"
					:value-type="ValueType.Date"
					:class="{ 'readonly-bordered-input': readonly }"
					:readonly
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ExistingMortgagePart } from "@loanlink-nl/model-app";
import { ValueType } from "@/enums/ValueType";
import { computed, ref, watch } from "vue";
import {
	asDisplayCur,
	formatNumeric,
	getInterestLabel,
} from "@/helpers/inputHelpers";
import { useCalculationDate } from "@/composables/useCalculationDate";
import { fuzzyDateParse } from "@loanlink-nl/migration";
import { repaymentFormOptions } from "./repayment-form";
import { getMonthSpan } from "@loanlink-nl/shared";
import { useDossierDataStore } from "@/store/useDossierDataStore";

const dossierDataStore = useDossierDataStore();

function modelToInput(value?: number): string {
	const minimumDecimals = value !== undefined && value % 1 === 0 ? 0 : 2;

	return formatNumeric(value, minimumDecimals, 2, true) ?? "";
}

const props = defineProps<{
	collapsed?: boolean;
	readonly?: boolean;
}>();
const part = defineModel<ExistingMortgagePart>();
const emit = defineEmits<{
	(e: "delete"): void;
}>();
const collapsed = ref(props.collapsed ?? true);

const interestDeductionLawOptions = [
	{ label: "Geen", value: "none" },
	{ label: "Oud", value: "old" },
	{ label: "Nieuw", value: "new" },
];

function partTitle(part: ExistingMortgagePart) {
	const parts = ["Leningdeel"];

	const repaymentLabel = part.repaymentForm
		? repaymentFormOptions.find((opt) => opt.value === part.repaymentForm)
				?.label
		: undefined;
	if (repaymentLabel) {
		parts.push(repaymentLabel);
	}

	if (part.amountCurrent) {
		parts.push(asDisplayCur(part.amountCurrent));
	}

	if (part.interestRate) {
		parts.push(getInterestLabel(part.interestRate));
	}

	if (part.lenderMortgagePartId) {
		parts.push(part.lenderMortgagePartId);
	}

	return parts.join(" - ");
}

const looptijd = computed({
	get() {
		if (!part.value?.startDate || !part.value?.endDate) {
			return;
		}

		const startDate = fuzzyDateParse(part.value.startDate);
		const endDate = fuzzyDateParse(part.value.endDate);

		if (!startDate.isValid || !endDate.isValid) {
			return;
		}

		return getMonthSpan(startDate, endDate);
	},
	set(value) {
		if (!part.value?.startDate || value === undefined) {
			return;
		}

		const startDate = fuzzyDateParse(part.value.startDate).startOf("month");
		const endDate = startDate.plus({ months: value }).toISODate();
		part.value.endDate = endDate ?? undefined;
	},
});

function calculateRemainingBalance(
	originalAmount?: number,
	interestRate?: number,
	startDate?: string,
	endDate?: string,
	passingDate?: string,
	repaymentForm?: string,
): number | undefined {
	if (originalAmount !== undefined) {
		switch (repaymentForm) {
			case "interest-only":
			case "life-insurance":
			case "investment-mortgage":
			case "savings-mortgage": {
				return originalAmount;
			}
		}
	}

	if (
		!originalAmount ||
		originalAmount <= 0 ||
		!startDate ||
		!endDate ||
		!passingDate ||
		!repaymentForm
	) {
		return undefined;
	}

	if (interestRate === undefined || interestRate < 0) {
		return undefined;
	}

	const start = fuzzyDateParse(startDate);
	const end = fuzzyDateParse(endDate);
	const passing = fuzzyDateParse(passingDate);

	if (!start.isValid || !end.isValid || !passing.isValid) {
		return undefined;
	}

	if (passing < start) {
		return originalAmount;
	}

	if (passing >= end) {
		return repaymentForm === "interest-only" ||
			repaymentForm === "life-insurance" ||
			repaymentForm === "investment-mortgage" ||
			repaymentForm === "savings-mortgage"
			? originalAmount
			: 0;
	}

	const totalMonths = getMonthSpan(start, end);
	const elapsedMonths = getMonthSpan(start, passing);

	if (
		totalMonths === undefined ||
		elapsedMonths === undefined ||
		totalMonths <= 0 ||
		elapsedMonths < 0
	) {
		return undefined;
	}

	const monthlyRate = interestRate / 12;

	switch (repaymentForm) {
		case "annuity": {
			if (monthlyRate === 0) {
				return Math.max(
					0,
					originalAmount - (originalAmount / totalMonths) * elapsedMonths,
				);
			}

			const denominator = Math.pow(1 + monthlyRate, totalMonths) - 1;
			if (denominator === 0) {
				return undefined;
			}

			const monthlyPayment =
				(originalAmount *
					monthlyRate *
					Math.pow(1 + monthlyRate, totalMonths)) /
				denominator;

			const compoundedPrincipal =
				originalAmount * Math.pow(1 + monthlyRate, elapsedMonths);
			const paidAmount =
				monthlyPayment *
				((Math.pow(1 + monthlyRate, elapsedMonths) - 1) / monthlyRate);
			const remainingBalance = compoundedPrincipal - paidAmount;

			return Math.max(0, Math.round(remainingBalance * 100) / 100);
		}

		case "lineair": {
			const monthlyPrincipalPayment = originalAmount / totalMonths;
			const remainingBalance =
				originalAmount - monthlyPrincipalPayment * elapsedMonths;
			return Math.max(0, Math.round(remainingBalance * 100) / 100);
		}

		case "interest-only":
		case "life-insurance":
		case "investment-mortgage":
		case "savings-mortgage": {
			return originalAmount;
		}

		default:
			return undefined;
	}
}

watch(
	() => part.value?.interestDeductionLaw,
	(law) => {
		if (law === "none" && part.value) {
			part.value.interestDeductionEndDate = undefined;
		}
	},
);

const { passingDateString } = useCalculationDate();

const calculatedAmountCurrent = computed(() => {
	const passeerdatum = passingDateString.value;
	if (!part.value || !passeerdatum) {
		return undefined;
	}

	const remainingBalance = calculateRemainingBalance(
		part.value.amountOriginal,
		part.value.interestRate,
		part.value.startDate,
		part.value.endDate,
		passeerdatum,
		part.value.repaymentForm,
	);

	return remainingBalance;
});

function applyAmountCurrent() {
	const newValue = calculatedAmountCurrent.value;
	if (part.value !== undefined) {
		part.value.amountCurrent = newValue;
	}
}
</script>
